import { UserRole } from '../types/auth';

const STORAGE_KEYS = {
  token: 'token',
  userId: 'user_id',
  role: 'role',
  tenantId: 'tenant_id',
  tenantSlug: 'tenant_slug',
  orgId: 'org_id',
  isPlatformAdmin: 'is_platform_admin'
} as const;

/**
 * Server-computed UI capabilities from `GET auth/session/` (backend
 * `authz.capabilities_for`). Each one mirrors the backend rule that enforces the
 * action it reveals, so UI gates read these instead of comparing role strings.
 * Never persisted to storage: they are always fetched from the server.
 */
export const CAPABILITY_NAMES = [
  'edit_departments',
  'view_admin_console',
  'view_record_owners',
  'platform_admin'
] as const;

export type CapabilityName = typeof CAPABILITY_NAMES[number];
export type Capabilities = Readonly<Record<CapabilityName, boolean>>;

/** Fail-closed default: no session (yet), a failed fetch, or signed out. */
export const NO_CAPABILITIES: Capabilities = Object.freeze({
  edit_departments: false,
  view_admin_console: false,
  view_record_owners: false,
  platform_admin: false
});

type StorageScope = 'local' | 'session';
type AuthNumberLike = number | string | null | undefined;
type AuthState = {
  token: string | null;
  userId: string | null;
  role: UserRole | null;
  tenantId: string | null;
  tenantSlug: string | null;
  orgId: string | null;
  isPlatformAdmin: boolean;
  /** From the server session for the current token; never read from storage. */
  capabilities: Capabilities;
  authenticated: boolean;
};

export type AuthSessionPayload = {
  authenticated: boolean;
  user_id?: AuthNumberLike;
  role?: UserRole | string | null;
  tenant_id?: AuthNumberLike;
  tenant_slug?: string | null;
  org_id?: AuthNumberLike;
  is_platform_admin?: boolean;
  capabilities?: Partial<Record<CapabilityName, unknown>> | null;
};

const STORAGE_SCOPES: StorageScope[] = ['local', 'session'];

const createRedirect = (to: string): Response => {
  return new Response(null, {
    status: 302,
    headers: {
      Location: to
    }
  });
};

const makeAuthState = (): AuthState => ({
  token: null,
  userId: null,
  role: null,
  tenantId: null,
  tenantSlug: null,
  orgId: null,
  isPlatformAdmin: false,
  capabilities: NO_CAPABILITIES,
  authenticated: false
});

let authState: AuthState = makeAuthState();
let authHydrated = false;

// In memory only. Capabilities belong to the token they were fetched for, so a
// token that changes (sign-out, sign-in, storage edits) sees none until its own
// session has been applied.
let sessionCapabilities: Capabilities = NO_CAPABILITIES;
let sessionToken: string | null = null;

function capabilitiesForToken(token: string | null): Capabilities {
  return token && token === sessionToken ? sessionCapabilities : NO_CAPABILITIES;
}

function resetSessionCapabilities(): void {
  sessionCapabilities = NO_CAPABILITIES;
  sessionToken = null;
}

function normalizeCapabilities(raw: AuthSessionPayload['capabilities']): Capabilities {
  const result = { ...NO_CAPABILITIES } as Record<CapabilityName, boolean>;
  if (raw && typeof raw === 'object') {
    for (const name of CAPABILITY_NAMES) {
      // Only an explicit `true` grants; anything else fails closed.
      result[name] = raw[name] === true;
    }
  }
  return Object.freeze(result);
}

function getStorage(scope: StorageScope): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return scope === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function toStringOrNull(value: AuthNumberLike): string | null {
  if (value === null || typeof value === 'undefined') {
    return null;
  }

  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeRole(role: string | null | undefined): UserRole | null {
  if (!role) {
    return null;
  }

  if ((Object.values(UserRole) as string[]).includes(role)) {
    return role as UserRole;
  }

  return null;
}

function readStoredValue(key: string): string | null {
  for (const scope of STORAGE_SCOPES) {
    const storage = getStorage(scope);
    const value = storage?.getItem(key);
    if (value) {
      return value;
    }
  }

  return null;
}

function getTokenStorageScope(): StorageScope | null {
  for (const scope of STORAGE_SCOPES) {
    const storage = getStorage(scope);
    if (storage?.getItem(STORAGE_KEYS.token)) {
      return scope;
    }
  }

  return null;
}

function persistValue(key: string, value: string | null, preferredScope: StorageScope | null): void {
  for (const scope of STORAGE_SCOPES) {
    const storage = getStorage(scope);
    if (!storage) {
      continue;
    }

    if (preferredScope === scope && value !== null) {
      storage.setItem(key, value);
    } else {
      storage.removeItem(key);
    }
  }
}

function clearStoredAuthState(): void {
  for (const key of Object.values(STORAGE_KEYS)) {
    persistValue(key, null, null);
  }
}

function recalculateAuthenticated(state: AuthState): boolean {
  return Boolean(state.token);
}

type AuthListener = () => void;
const authListeners = new Set<AuthListener>();

export function subscribeAuthChange(listener: AuthListener): () => void {
  authListeners.add(listener);
  return () => { authListeners.delete(listener); };
}

function setState(patch: Partial<AuthState>): void {
  authState = {
    ...authState,
    ...patch
  };
  authState.authenticated = recalculateAuthenticated(authState);
  authListeners.forEach(fn => fn());
}

function readStateFromStorage(): AuthState {
  const token = readStoredValue(STORAGE_KEYS.token);

  return {
    token,
    userId: readStoredValue(STORAGE_KEYS.userId),
    role: normalizeRole(readStoredValue(STORAGE_KEYS.role)),
    tenantId: readStoredValue(STORAGE_KEYS.tenantId),
    tenantSlug: readStoredValue(STORAGE_KEYS.tenantSlug),
    orgId: readStoredValue(STORAGE_KEYS.orgId),
    isPlatformAdmin: readStoredValue(STORAGE_KEYS.isPlatformAdmin) === 'true',
    capabilities: capabilitiesForToken(token),
    authenticated: Boolean(token)
  };
}

function ensureAuthStateHydrated(): void {
  authState = readStateFromStorage();
  authHydrated = true;
}

export const bootstrapAuthState = (): AuthState => {
  authState = readStateFromStorage();
  authHydrated = true;
  return { ...authState };
};

export const clearAuthState = (): void => {
  clearStoredAuthState();
  resetSessionCapabilities();
  authState = makeAuthState();
  authHydrated = true;
  authListeners.forEach(fn => fn());
};

export const getAuthState = (): AuthState => {
  ensureAuthStateHydrated();
  return { ...authState };
};

export const getAuthToken = (): string | null => {
  ensureAuthStateHydrated();
  return authState.token;
};

export const setAuthToken = (token: string, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const scope: StorageScope = isRemember ? 'local' : 'session';
  persistValue(STORAGE_KEYS.token, token, scope);
  setState({ token });
};

export const deleteAuthToken = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.token, null, null);
  setState({ token: null });
};

export const getTenantId = (): string | null => {
  ensureAuthStateHydrated();
  return authState.tenantId;
};

export const setTenantId = (tenantId: AuthNumberLike, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const normalized = toStringOrNull(tenantId);
  persistValue(STORAGE_KEYS.tenantId, normalized, isRemember ? 'local' : 'session');
  setState({ tenantId: normalized });
};

export const deleteTenantId = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.tenantId, null, null);
  setState({ tenantId: null });
};

export const getTenantSlug = (): string | null => {
  ensureAuthStateHydrated();
  return authState.tenantSlug;
};

export const setTenantSlug = (tenantSlug: string | null | undefined, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const normalized = tenantSlug ? tenantSlug.trim() || null : null;
  persistValue(STORAGE_KEYS.tenantSlug, normalized, isRemember ? 'local' : 'session');
  setState({ tenantSlug: normalized });
};

export const deleteTenantSlug = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.tenantSlug, null, null);
  setState({ tenantSlug: null });
};

export const getOrgId = (): string | null => {
  ensureAuthStateHydrated();
  return authState.orgId;
};

export const setOrgId = (orgId: AuthNumberLike, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const normalized = toStringOrNull(orgId);
  persistValue(STORAGE_KEYS.orgId, normalized, isRemember ? 'local' : 'session');
  setState({ orgId: normalized });
};

export const deleteOrgId = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.orgId, null, null);
  setState({ orgId: null });
};

export const getIsPlatformAdmin = (): boolean => {
  ensureAuthStateHydrated();
  return authState.isPlatformAdmin;
};

export const setIsPlatformAdmin = (isPlatformAdmin: boolean, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const normalized = Boolean(isPlatformAdmin);
  persistValue(STORAGE_KEYS.isPlatformAdmin, normalized ? 'true' : null, isRemember ? 'local' : 'session');
  setState({ isPlatformAdmin: normalized });
};

export const deleteIsPlatformAdmin = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.isPlatformAdmin, null, null);
  setState({ isPlatformAdmin: false });
};

export const getUserId = (): string | null => {
  ensureAuthStateHydrated();
  return authState.userId;
};

export const setUserId = (userId: AuthNumberLike, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  const normalized = toStringOrNull(userId);
  persistValue(STORAGE_KEYS.userId, normalized, isRemember ? 'local' : 'session');
  setState({ userId: normalized });
};

export const deleteUserId = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.userId, null, null);
  setState({ userId: null });
};

export const setUserRole = (role: UserRole, isRemember: boolean = false): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.role, role, isRemember ? 'local' : 'session');
  setState({ role });
};

export const deleteUserRole = (): void => {
  ensureAuthStateHydrated();
  persistValue(STORAGE_KEYS.role, null, null);
  setState({ role: null });
};

export const getUserRole = (): UserRole | null => {
  ensureAuthStateHydrated();
  return authState.role;
};

export const setAuthSession = (session: AuthSessionPayload): void => {
  ensureAuthStateHydrated();

  if (!session) {
    return;
  }

  if (session.authenticated === false) {
    clearAuthState();
    return;
  }

  const scope = getTokenStorageScope();
  const patch: Partial<AuthState> = {};

  if (Object.prototype.hasOwnProperty.call(session, 'user_id')) {
    patch.userId = toStringOrNull(session.user_id);
    if (scope) {
      persistValue(STORAGE_KEYS.userId, patch.userId ?? null, scope);
    }
  }

  if (Object.prototype.hasOwnProperty.call(session, 'role')) {
    const normalizedRole = normalizeRole(typeof session.role === 'string' ? session.role : null);
    patch.role = normalizedRole;
    if (scope) {
      persistValue(STORAGE_KEYS.role, normalizedRole, scope);
    }
  }

  if (Object.prototype.hasOwnProperty.call(session, 'tenant_id')) {
    patch.tenantId = toStringOrNull(session.tenant_id);
    if (scope) {
      persistValue(STORAGE_KEYS.tenantId, patch.tenantId ?? null, scope);
    }
  }

  if (Object.prototype.hasOwnProperty.call(session, 'tenant_slug')) {
    patch.tenantSlug = session.tenant_slug ? session.tenant_slug.trim() || null : null;
    if (scope) {
      persistValue(STORAGE_KEYS.tenantSlug, patch.tenantSlug ?? null, scope);
    }
  }

  if (Object.prototype.hasOwnProperty.call(session, 'org_id')) {
    patch.orgId = toStringOrNull(session.org_id);
    if (scope) {
      persistValue(STORAGE_KEYS.orgId, patch.orgId ?? null, scope);
    }
  }

  if (Object.prototype.hasOwnProperty.call(session, 'is_platform_admin')) {
    const normalizedIsPlatformAdmin = Boolean(session.is_platform_admin);
    patch.isPlatformAdmin = normalizedIsPlatformAdmin;
    if (scope) {
      persistValue(STORAGE_KEYS.isPlatformAdmin, normalizedIsPlatformAdmin ? 'true' : null, scope);
    }
  }

  // Bind the capabilities to the token this session was fetched with.
  sessionToken = authState.token;
  sessionCapabilities = normalizeCapabilities(session.capabilities);
  patch.capabilities = capabilitiesForToken(authState.token);

  setState(patch);
};

/** True once a server session has been applied for the current token. */
export const isAuthSessionHydrated = (): boolean => {
  ensureAuthStateHydrated();
  return Boolean(authState.token) && authState.token === sessionToken;
};

export const getCapabilities = (): Capabilities => {
  ensureAuthStateHydrated();
  return authState.capabilities;
};

/**
 * Plain (non-hook) capability check. False until the server session for the
 * current token has been applied, and false after a failed fetch.
 */
export const hasCapability = (name: CapabilityName): boolean => {
  return getCapabilities()[name] === true;
};

export const checkAuthLoader = (): Response | null => {
  ensureAuthStateHydrated();

  if (!authState.authenticated) {
    return createRedirect('/signin');
  }

  return null;
};

export const checkNotAuthLoader = (): Response | null => {
  ensureAuthStateHydrated();

  if (authState.authenticated) {
    return createRedirect('/data-flows');
  }

  return null;
};
